import type { PayloadAction } from '@reduxjs/toolkit';
import { createReducer } from '@reduxjs/toolkit';
import {
  addCategories,
  createOutcomeSuccess,
  deleteCategories,
  editCategory,
  fetchAllCategoriesSuccess,
  selectCustomerBulkOutcomes,
  selectFromCustomerBulkOutcomes,
  selectUseCaseBulkOutcomes,
  setRules,
  upsertBulkCategoriesFailure,
  upsertBulkCategoriesRequest,
  upsertBulkCategoriesSuccess,
} from 'actions';

import type {
  API,
  Category,
  Customer,
  ErrorObject,
  MModel,
  Model,
  MOutcome,
  NormalizedResource,
  RuleGroup,
  UUID,
} from 'types';
import { v4 } from 'uuid';

export type BulkOutcomesState = {
  models: NormalizedResource<MModel>;
  rules: Record<string, { rule_revision_uuid: string; rule: RuleGroup; categories: Category[] }>;
  customer: Customer | null;
  fromCustomer: UUID | null;
  useCase: Model | null;
  loading: string[];
  error: ErrorObject | null;
};
type BulkOutcomesReducer<P = void> = (
  state: BulkOutcomesState,
  action: PayloadAction<P>
) => BulkOutcomesState;

const defaultState: BulkOutcomesState = {
  models: {},
  rules: {},
  customer: null,
  fromCustomer: '',
  useCase: null,
  loading: [],
  error: null,
};

const handleUpsertBulkCategoriesRequest: BulkOutcomesReducer = (state) => ({
  ...state,
  loading: [...state.loading, upsertBulkCategoriesRequest.toString()],
});

const handleUpsertBulkCategoriesFailure: BulkOutcomesReducer<ErrorObject> = (
  state,
  { payload }
) => ({
  ...state,
  error: payload,
  loading: state.loading.filter((s) => s !== upsertBulkCategoriesRequest.toString()),
});

const handleUpsertBulkCategoriesSuccess: BulkOutcomesReducer = (state) => ({
  ...state,
  loading: state.loading.filter((s) => s !== upsertBulkCategoriesRequest.toString()),
});

const handleEditCategory: BulkOutcomesReducer<{ category: Category; ruleId: string }> = (
  state,
  { payload }
) => ({
  ...state,
  rules: {
    ...state.rules,
    [payload.ruleId]: {
      ...state.rules[payload.ruleId],
      categories: state.rules[payload.ruleId].categories?.map((c) =>
        c.uuid === payload.category.uuid ? payload.category : c
      ),
    },
  },
});

const handleAddCategories: BulkOutcomesReducer<{
  categories: Category[];
  rule: RuleGroup;
  rule_revision_uuid: string;
}> = (state, { payload }) => ({
  ...state,
  rules: {
    ...state.rules,
    [payload.rule.uuid]: {
      rule_revision_uuid: payload.rule_revision_uuid,
      rule: payload.rule,
      categories: [...state.rules[payload.rule.uuid].categories, ...payload.categories],
    },
  },
});

const handleDeleteCategories: BulkOutcomesReducer<{ categories: string[]; ruleId: string }> = (
  state,
  { payload }
) => ({
  ...state,
  rules: {
    ...state.rules,
    [payload.ruleId]: {
      ...state.rules[payload.ruleId],
      categories: state.rules[payload.ruleId].categories?.filter(
        (c) => !payload.categories.includes(c.uuid)
      ),
    },
  },
});

const handleSetRules: BulkOutcomesReducer<
  { rule_revision_uuid: string; rule: RuleGroup; categories: Category[] }[]
> = (state, { payload }) => {
  const newRules: Record<
    string,
    { rule_revision_uuid: string; rule: RuleGroup; categories: Category[] }
  > = {};
  payload.forEach((r) => {
    newRules[r.rule.uuid] = r;
  });

  return {
    ...state,
    rules: {
      ...newRules,
    },
  };
};

const handleFetchAllCategoriesSuccess: BulkOutcomesReducer<API.WrappedAPIResponse<Category>> = (
  state,
  { payload }
) => {
  const customer = { ...state.customer };

  const categories = payload.records;

  const newRules: Record<
    string,
    { rule_revision_uuid: string; rule: RuleGroup; categories: Category[] }
  > = {};

  categories.forEach((c) => {
    if (c.rule_head && c.rule_head.rule_group) {
      newRules[c.rule_head.rule_group_uuid] = {
        rule_revision_uuid: c.rule_revision_uuid,
        rule: c.rule_head.rule_group,
        categories: [
          ...(newRules[c.rule_head.rule_group_uuid]?.categories || []),
          ...(c.customer_uuid === customer.uuid ? [c] : []),
        ],
      };
    }
  });

  return {
    ...state,
    rules: {
      ...newRules,
    },
  };
};

const handleCreateOutcomeSuccess: BulkOutcomesReducer<{
  outcome: NormalizedResource<MOutcome>;
  ruleId?: string;
  modelId?: string;
  customerId?: string;
}> = (state, { payload }) => {
  const { customerId, modelId, ruleId } = payload;

  const newRules: Record<
    string,
    { rule_revision_uuid: string; rule: RuleGroup; categories: Category[] }
  > = {};
  const outcome = Object.values(payload.outcome)[0];

  if (
    Object.values(state.rules).some((r) =>
      r.categories.some((c) => c.outcome_uuid === outcome.uuid)
    )
  ) {
    Object.values(state.rules).forEach((r) => {
      const updatedCategories = r.categories.map((c) =>
        c.outcome_uuid === outcome.uuid ? { ...c, outcome } : c
      );

      newRules[r.rule.uuid] = {
        rule_revision_uuid: r.rule_revision_uuid,
        rule: r.rule,
        categories: updatedCategories,
      };
    });
  } else if (ruleId) {
    const { rule, rule_revision_uuid: revisionId } = state.rules[ruleId];

    const newCategory = {
      uuid: v4(),
      config: {
        version: '0.1',
        active_gmail: false,
        active_chrome: false,
        active_slack: false,
        active_o365: false,
        active_o365_teams: false,
        active_zendesk: false,
        active_glia: false,
        active_outbound: false,
        active_inbound: false,
        active_api: false,
        active_app: false,
        active_realtime: false,
        active_attachment_extensions: null,
        valid_for_seconds: null,
        cooldown_time_seconds: null,
      } as Category['config'],
      outcome_uuid: outcome.uuid,
      outcome,
      rule_revision_uuid: revisionId,
      model_uuid: modelId || '',
      customer_uuid: customerId || '',
      rule,
    } as unknown as Category;

    newRules[ruleId] = {
      ...state.rules[ruleId],
      categories: [...state.rules[ruleId].categories, newCategory],
    };
  }

  return {
    ...state,
    rules: {
      ...state.rules,
      ...newRules,
    },
  };
};

const handleSelectCustomer: BulkOutcomesReducer<Customer> = (state, { payload }) => ({
  ...state,
  customer: payload,
});

const handleSelectFromCustomer: BulkOutcomesReducer<UUID> = (state, { payload }) => ({
  ...state,
  fromCustomer: payload,
});

const handleSelectUseCase: BulkOutcomesReducer<Model> = (state, { payload }) => ({
  ...state,
  useCase: payload,
});

const handlers = {
  [editCategory.toString()]: handleEditCategory,
  [addCategories.toString()]: handleAddCategories,
  [deleteCategories.toString()]: handleDeleteCategories,
  [setRules.toString()]: handleSetRules,
  [fetchAllCategoriesSuccess.toString()]: handleFetchAllCategoriesSuccess,
  [selectCustomerBulkOutcomes.toString()]: handleSelectCustomer,
  [selectFromCustomerBulkOutcomes.toString()]: handleSelectFromCustomer,
  [selectUseCaseBulkOutcomes.toString()]: handleSelectUseCase,

  [upsertBulkCategoriesRequest.toString()]: handleUpsertBulkCategoriesRequest,
  [upsertBulkCategoriesSuccess.toString()]: handleUpsertBulkCategoriesSuccess,
  [upsertBulkCategoriesFailure.toString()]: handleUpsertBulkCategoriesFailure,

  [createOutcomeSuccess.toString()]: handleCreateOutcomeSuccess,
};

const modelsReducer = createReducer(defaultState, handlers);

export default modelsReducer;
